<template>
  <div ref="rootRef" class="chat-search-field">
    <!-- People are a facet, not free text: they sit in the field as chips so the
         active filter is readable by name and removable one at a time. Picking a
         group drops its members in here, so a group is editable person by person. -->
    <!-- Past a handful the names are what overflow, not the faces: everyone keeps
         a chip, but they shrink to bare avatars so a 21-person group still reads
         as one row. The title carries the name either way. -->
    <button
      v-for="user in selectedUsers"
      :key="user.id"
      type="button"
      class="chat-search-field__chip"
      :class="{ 'chat-search-field__chip--compact': compactChips }"
      :title="`Stop filtering by ${user.name}`"
      @click="remove(user.id)"
    >
      <user-avatar :name="user.name" :url="user.photo" size="x-small" />
      <template v-if="!compactChips">
        {{ shortName(user) }}
        <span class="chat-search-field__chip-remove" v-html="crossIcon" />
      </template>
    </button>

    <input
      ref="inputRef"
      v-model="query"
      class="chat-search-field__input"
      type="text"
      :placeholder="selected.length ? 'Search messages, or @…' : 'Search messages, or @ to filter by person or group'"
      @focus="focused = true"
      @blur="focused = false"
      @keydown.down.prevent="moveHighlight(1)"
      @keydown.up.prevent="moveHighlight(-1)"
      @keydown.enter="pickHighlighted"
      @keydown.backspace="removeLastOnEmpty"
    />

    <button v-if="selected.length || query" type="button" class="chat-search-field__clear" @click="clearAll">
      Clear
    </button>

    <fs-popper
      :reference="rootRef"
      :show="dropdownOpen"
      :close-on-outside-click="false"
      placement="bottom-start"
      :offset="[0, 6]"
      animation="none"
      no-styling
    >
      <ul class="chat-search-field__results">
        <li v-for="(candidate, index) in results" :key="`${candidate.kind}-${candidate.id}`">
          <!-- mousedown.prevent keeps focus on the input, so blur does not close
               the list out from under the click. -->
          <button
            type="button"
            class="chat-search-field__result"
            :class="{ 'chat-search-field__result--active': index === highlight }"
            @mousedown.prevent
            @mouseenter="highlight = index"
            @click="pick(candidate)"
          >
            <template v-if="candidate.kind === 'user'">
              <user-avatar :name="candidate.user.name" :url="candidate.user.photo" size="x-small" />
              {{ candidate.user.name }}
            </template>
            <template v-else>
              <span class="chat-search-field__role-chip">@{{ candidate.role.name }}</span>
              <span class="chat-search-field__result-meta">
                {{ candidate.memberCount }} {{ candidate.memberCount === 1 ? 'person' : 'people' }}
              </span>
            </template>
          </button>
        </li>
      </ul>
    </fs-popper>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { RoleDto, UserDto } from '@/ApiClient';
import { useChat } from '@/store/chat';
import { useBrandUsers } from '@/store/brand-users';
import { useRoles } from '@/store/roles';
import { whereNotNull } from '@lib/type-helpers';
import { splitSearchMention } from '@lib/chat-mentions';
import UserAvatar from '@components/user/user-avatar.vue';
import FsPopper from '@components/poppers/fs-popper.vue';
import crossIcon from '@icons/other/ico-cross.svg';

const selected = defineModel<string[]>({ required: true });
const query = defineModel<string>('query', { required: true });

const rootRef = ref<HTMLElement>();
const inputRef = ref<HTMLInputElement>();
const focused = ref(false);

const selectedUsers = computed(() => selected.value.map((id) => useBrandUsers().getById(id)).filter(whereNotNull));

const shortName = (user: UserDto) => (user.id === useChat().meId ? 'You' : user.name.split(' ')[0]);

// Most groups are three people or fewer, so the named chips are what you nearly
// always see; the compact form is for the tail (the largest group is 21).
const CHIP_LIMIT = 5;
const compactChips = computed(() => selectedUsers.value.length > CHIP_LIMIT);

// An "@" is an explicit request for the people list, so a bare one offers
// everybody, and anything after it narrows that list rather than the messages.
const mentionToken = computed(() => splitSearchMention(query.value).mention);

// Prefix, not substring: matching anywhere inside a name pops the list open on
// ordinary message text ("an" is inside half the brand). Any word counts, so
// "gus" still finds Robin Gustafsson.
const matchesName = (name: string, text: string): boolean => {
  const lower = name.toLowerCase();
  return lower.startsWith(text) || lower.split(/\s+/).some((word) => word.startsWith(text));
};

type Candidate =
  | { kind: 'user'; id: string; user: UserDto }
  | { kind: 'role'; id: number; role: RoleDto; members: UserDto[]; memberCount: number };

// Groups lead, the way they do in the composer's mention list: there are few of
// them, and one stands in for several people, so it is the bigger lever.
const results = computed<Candidate[]>(() => {
  const token = mentionToken.value;
  const text = (token ?? query.value).trim().toLowerCase();
  // Without an "@" there has to be something to match: the field is a message
  // search first, and offering the whole brand on focus would bury the feed.
  if (token === null && !text) return [];

  const takenUsers = new Set(selected.value);

  const roles: Candidate[] = useRoles()
    .loaded.filter((role) => !text || matchesName(role.name, text))
    .map((role) => ({ kind: 'role' as const, id: role.id, role, members: useRoles().usersForRole(role.id) }))
    // A group whose every member is already a chip has nothing left to add.
    .filter(({ members }) => members.length > 0 && members.some((member) => !takenUsers.has(member.id)))
    .map(({ role, members }) => ({ kind: 'role', id: role.id, role, members, memberCount: members.length }));

  const users: Candidate[] = useBrandUsers()
    .loaded.filter((user) => !takenUsers.has(user.id) && (!text || matchesName(user.name, text)))
    .map((user) => ({ kind: 'user', id: user.id, user }));

  return [...roles, ...users].slice(0, 8);
});

const dropdownOpen = computed(() => focused.value && results.value.length > 0);

// After an "@" the intent is already to pick someone, so Enter takes the top
// match. Plain text starts unhighlighted, leaving Enter to the message search.
const highlight = ref(-1);
watch(query, () => (highlight.value = mentionToken.value === null ? -1 : 0));

const moveHighlight = (step: number) => {
  if (!results.value.length) return;
  const next = highlight.value + step;
  highlight.value = next < 0 ? results.value.length - 1 : next % results.value.length;
};

const pickHighlighted = () => {
  const candidate = highlight.value >= 0 ? results.value[highlight.value] : undefined;
  if (candidate) pick(candidate);
};

// A group resolves to its members here rather than being carried as a group:
// the filter stays one flat set of people, and you can drop the one member you
// did not mean. The composer expands a role the same way when it sends.
const pick = (candidate: Candidate) => {
  const added = candidate.kind === 'user' ? [candidate.id] : candidate.members.map((member) => member.id);
  selected.value = [...new Set([...selected.value, ...added])];

  // Drop only the "@…" the pick answered, so "designer @fel" keeps searching
  // for "designer". Without an "@" the whole query was the name.
  const { search, mention } = splitSearchMention(query.value);
  query.value = mention === null ? '' : search;
};

const remove = (id: string) => {
  selected.value = selected.value.filter((userId) => userId !== id);
};

const removeLastOnEmpty = () => {
  if (!query.value && selected.value.length) selected.value = selected.value.slice(0, -1);
};

const clearAll = () => {
  selected.value = [];
  query.value = '';
};

defineExpose({ focus: () => inputRef.value?.focus() });
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.chat-search-field {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  min-height: 3.8rem;
  padding: 0.5rem 1.2rem;
  box-sizing: border-box;
  border: 1px solid $c-hairline;
  border-radius: $br-heavy;
  background: $c-surface;

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    height: 2.6rem;
    padding: 0 0.8rem 0 0.4rem;
    border: none;
    border-radius: $br-pill;
    background: $c-surface-muted;
    cursor: pointer;
    font-family: inherit;
    font-size: 1.2rem;
    color: $c-ink;

    &:hover {
      background: rgba($c-ink, 0.12);
    }

    :deep(.user-avatar),
    :deep(.user-avatar__fallback) {
      width: 1.8rem;
      min-width: 1.8rem;
      height: 1.8rem;
      font-size: 0.9rem;
    }
  }

  // Bare avatar, no pill: at this count the pills would be most of the field.
  // Removal stays a click, and the title says whose face it is.
  &__chip--compact {
    padding: 0;
    background: none;
    transition: opacity $transition-fast ease;

    &:hover {
      background: none;
      opacity: 0.5;
    }

    :deep(.user-avatar),
    :deep(.user-avatar__fallback) {
      width: 2.2rem;
      min-width: 2.2rem;
      height: 2.2rem;
    }
  }

  &__chip-remove {
    display: flex;

    :deep(svg) {
      width: 0.8rem;
      height: 0.8rem;
      fill: $c-ink-soft;
    }
  }

  &__input {
    flex: 1;
    // Wraps to its own line rather than being squeezed to nothing once the
    // chips fill the row.
    min-width: 12rem;
    height: 2.6rem;
    border: none;
    background: none;
    outline: none;
    font-family: inherit;
    font-size: 1.3rem;
    color: $c-ink;

    &::placeholder {
      color: rgba($c-ink, 0.45);
    }
  }

  &__clear {
    flex: none;
    margin-left: auto;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
    font-size: 1.1rem;
    font-weight: 700;
    color: $c-ink-soft;

    &:hover {
      color: $c-ink;
    }
  }

  &__results {
    width: 26rem;
    max-height: 24rem;
    margin: 0;
    padding: 0.4rem 0;
    overflow-y: auto;
    list-style: none;
    border-radius: $br-very-heavy;
    background: $c-surface;
    box-shadow: 0 1.2rem 3.2rem rgba($c-ink, 0.18);
  }

  &__result {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
    padding: 0.7rem 1.4rem;
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
    font-size: 1.3rem;
    text-align: left;
    color: $c-ink;

    &--active {
      background: $c-surface-muted;
    }

    :deep(.user-avatar),
    :deep(.user-avatar__fallback) {
      width: 2.4rem;
      min-width: 2.4rem;
      height: 2.4rem;
      font-size: 1rem;
    }
  }

  &__role-chip {
    padding: 0.2rem 0.8rem;
    border-radius: $br-pill;
    background: rgba($c-blue, 0.1);
    color: $c-on-info-soft;
    font-size: 1.2rem;
    font-weight: 700;
  }

  &__result-meta {
    font-size: 1.1rem;
    color: rgba($c-ink, 0.5);
  }
}
</style>
